import React, { useState, useEffect,useRef } from "react";
import { useParams } from "react-router-dom";
import { AiOutlineMessage, AiOutlineHistory, AiOutlineMenu, AiOutlineClose, AiOutlineEllipsis } from "react-icons/ai"; // Importing icons from react-icons
import { Edit, Pencil, Download, Delete, Trash, User, Settings, Palette, Sun, Moon, Monitor, LogOut, Info } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { darkenColor } from "../../constants/darkenColor";
import "react-tooltip/dist/react-tooltip.css";
import { fetchChatHistory, updateChatTitle } from '../../services/api/KaiConfigData';
import { isLightColor } from "../../utils//LuminosityChecker";

type ChatData = {
  id: string;
  created_at: string;
  messages: { sender: 'user' | 'assistant'; message: string }[];
  title: string;
};

interface ChatSidebarProps {
  chats: ChatData[];
  onSelectChat: (id: string) => void;
  configData: any;
  onNewChat: () => void;
  activeChatId: string;
  toggleSidebar: () => void;
  isSidebarOpen: boolean;
  updateChatHistory: (app_id: string, net_id: string, chat_id:string) => void,
  onDelete: (chatId: string) => void; 
  isPreview: any,
  onLogout: () => void;
  userMsgCount: number;
  bypassMode: boolean | null;
  noAuth: boolean | null;
  netId: string;
  themeOverride: 'light' | 'dark' | 'auto';
  setThemeOverride: (theme: 'light' | 'dark' | 'auto') => void;
}

const lightenColor = (color: string, percent: number) => {
  // Check if the color is white
  if (color.toUpperCase() === "#FFFFFF") {
    if(percent === 1)
      return "#D3D3D3"; // Return a light gray color when the color is white
    else  
      return "#e7e7e7";
  }

  // Otherwise, continue lightening the color as usual
  if (!color.startsWith("#")) return color; // Ensure it's a valid hex color

  let num = parseInt(color.slice(1), 16),
    amt = Math.round(2.55 * percent),
    r = (num >> 16) + amt,
    g = ((num >> 8) & 0x00ff) + amt,
    b = (num & 0x0000ff) + amt;

  return `rgb(${Math.min(r, 255)}, ${Math.min(g, 255)}, ${Math.min(b, 255)})`;
};


const ChatSidebar: React.FC<ChatSidebarProps> = ({ chats, onSelectChat, configData, onNewChat, activeChatId, toggleSidebar,isSidebarOpen,updateChatHistory,onDelete,isPreview, onLogout, userMsgCount, bypassMode, noAuth, netId, themeOverride, setThemeOverride }) => {
  // const [isSidebarOpen, setIsSidebarOpen] = useState(true); // State to control sidebar visibility
  const { app_id }  = useParams() ;
  const net_id = localStorage.getItem('net_id') || '';
  const [chatList, setChatList] = useState<ChatData[]>(chats); // Manage the chat list in state
  const [isLoading, setIsLoading] = useState(false);
  const [longTouch, setLongTouch] = useState(false); 

  const app_ui_settings = configData && configData?.app_ui_settings;

  // Theme state is now passed as props from parent component
  const [userProfileOpen, setUserProfileOpen] = useState(false);
  const [collapsedModalOpen, setCollapsedModalOpen] = useState(false);

  // Color scheme using configData as default with theme override option
  const backgroundColor = configData?.app_ui_settings.ui_backgroundcolor;
  const configIsLight = backgroundColor ? isLightColor(backgroundColor) : true;
  
  // Determine effective theme
  const effectiveIsLight = themeOverride === 'auto' ? configIsLight : themeOverride === 'light';

  // Create subtle sidebar variation for any config color - ChatGPT style
  const createSubtleVariation = (baseColor: string, isLight: boolean) => {
    // Fallback colors if no valid baseColor
    if (!baseColor || !baseColor.startsWith("#")) {
      return isLight ? '#f7f7f8' : '#171717';
    }
    
    const hex = baseColor.slice(1);
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    
    // Very subtle adjustment - only 2-4 points
    const adjustment = isLight ? -3 : 3;
    const newR = Math.max(0, Math.min(255, r + adjustment));
    const newG = Math.max(0, Math.min(255, g + adjustment));
    const newB = Math.max(0, Math.min(255, b + adjustment));
    
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  const sidebarBackground = themeOverride === 'auto' 
    ? createSubtleVariation(backgroundColor, configIsLight)
    : (effectiveIsLight ? '#f7f7f8' : '#171717');
  
  const createHoverVariation = (baseColor: string, isLight: boolean) => {
    if (!baseColor || !baseColor.startsWith("#")) {
      return isLight ? '#f0f0f0' : '#2a2a2a';
    }
    
    const hex = baseColor.slice(1);
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    
    // Slightly more noticeable for hover - 8-12 points
    const adjustment = isLight ? -10 : 10;
    const newR = Math.max(0, Math.min(255, r + adjustment));
    const newG = Math.max(0, Math.min(255, g + adjustment));
    const newB = Math.max(0, Math.min(255, b + adjustment));
    
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  const hoverColor = themeOverride === 'auto'
    ? createHoverVariation(backgroundColor, configIsLight)
    : (effectiveIsLight ? '#f0f0f0' : '#2a2a2a');
    
  const createActiveVariation = (baseColor: string, isLight: boolean) => {
    if (!baseColor || !baseColor.startsWith("#")) {
      return isLight ? '#e5e5e5' : '#343541';
    }
    
    const hex = baseColor.slice(1);
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    
    // More noticeable for active state - 15-20 points
    const adjustment = isLight ? -18 : 18;
    const newR = Math.max(0, Math.min(255, r + adjustment));
    const newG = Math.max(0, Math.min(255, g + adjustment));
    const newB = Math.max(0, Math.min(255, b + adjustment));
    
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  const activeColor = themeOverride === 'auto'
    ? createActiveVariation(backgroundColor, configIsLight)
    : (effectiveIsLight ? '#e5e5e5' : '#343541');
    
  const textColor = themeOverride === 'auto'
    ? (configData?.app_ui_settings.ui_fontcolor || (effectiveIsLight ? '#333333' : '#ececf1'))
    : (effectiveIsLight ? '#333333' : '#ececf1');
    
  const borderColor = themeOverride === 'auto'
    ? createSubtleVariation(backgroundColor, configIsLight)
    : (effectiveIsLight ? '#e5e5e5' : '#32323b');


  // Add back the missing variables for backwards compatibility
  const darkerTabBackground = activeColor;

  const [editingChatId, setEditingChatId] = useState(null);
  const [newTitle, setNewTitle] = useState("");

  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null); // Track hovered chat for 3-dot menu visibility
  const [dropdownVisible, setDropdownVisible] = useState<any>(null); // Track which chat has the dropdown open
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef1 = useRef<HTMLDivElement | null>(null);
  const userProfileRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: any; bottom: any }>({
    top: null,
    bottom: null,
  });
  const chatRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  const sidebarRef = useRef<HTMLDivElement | null>(null);

  

  const today = new Date().toISOString().split('T')[0]; // Today's date
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayDate = yesterday.toISOString().split('T')[0]; // Yesterday's date

  const sevenDaysAgo = new Date();
sevenDaysAgo.setDate(yesterday.getDate() - 7);
const sevenDaysAgoDate = sevenDaysAgo.toISOString().split('T')[0]; 

  // Sort chats by date and time (full timestamp)
  const sortedChats = (chats: ChatData[]) => {
    // Sort chats in descending order based on the full timestamp (most recent first)
    return chats.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  };
  

  const todayChats = sortedChats(chatList.filter(chat => chat.created_at.startsWith(today)));
  const yesterdayChats = sortedChats(chatList.filter(chat => chat.created_at.startsWith(yesterdayDate)));

  // Filter chats that are within the last 7 days
const last7DaysChats = sortedChats(
  chatList.filter(chat => {
    const chatDate = chat.created_at.split('T')[0]; // Get the date part of the timestamp
    return chatDate >= sevenDaysAgoDate && chatDate < yesterdayDate;
  })
);

const chatSections = [
  { label: "Today", chats: todayChats },
  { label: "Yesterday", chats: yesterdayChats },
  { label: "Previous 7 Days", chats: last7DaysChats },
];


// useEffect(() => {
//   // Function to handle the sidebar toggle based on window size
//   const handleResize = () => {
//     // alert(window.innerWidth)
//     if (window.innerWidth <= 768) {
//       toggleSidebar() // Close sidebar on mobile
//     } else {
//       toggleSidebar() // Optionally keep it open on larger screens
//     }
//   };

//   // Run on load
//   handleResize();

//   // Attach resize event listener
//   window.addEventListener('resize', handleResize);

//   // Cleanup the event listener on component unmount
//   return () => {
//     window.removeEventListener('resize', handleResize);
//   };
// }, []); // Empty dependency array to run once when component mounts

useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownVisible(null); // Close dropdown if clicked outside
    }
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      toggleSidebar(); // Close sidebar when clicking outside
    }
    if (
      userProfileRef.current &&
      !userProfileRef.current.contains(event.target as Node) &&
      userProfileOpen
    ) {
      setUserProfileOpen(false); // Close user profile when clicking outside
    }
  };

  if (isSidebarOpen && window.innerWidth <= 768) {
    if(!editingChatId){
      document.addEventListener("mousedown", handleClickOutside);
    }
  }
  
  // Add separate listener for user profile modal that works on all screen sizes
  if (userProfileOpen && !editingChatId) {
    document.addEventListener("mousedown", handleClickOutside);
  }
  
  return () => {
    if(!editingChatId){
      document.removeEventListener("mousedown", handleClickOutside);
    }
  };
}, [isSidebarOpen, setEditingChatId, editingChatId, userProfileOpen]);


  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const diffInMs = today.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 3600 * 24));

    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays <= 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString(); // Format as a standard date
    }
  };

  const handleNewChat = () => {
    // Create a new chat object with a unique ID and an initial message
    const existingNullChat = chats.find(chat => chat.id === 'chat');
    if (existingNullChat) {
      onSelectChat(existingNullChat.id);
    }
    else{
    const newChat: ChatData = {
      id: 'chat', // Generate a unique ID based on the length
      created_at: new Date().toISOString(), // Set the current date
      title: 'chat',
      messages: [],
    };

    // Add the new chat to the list, ensuring no merging with existing chats
    setChatList(prevChats => [...prevChats, newChat]);

    // Call the parent `onNewChat` to handle other side effects
    onNewChat();

    // Optionally select the new chat
    onSelectChat(newChat.id);
  }
  };

  // Handle selecting a chat and update the active chat
  const handleChatSelect = (chatId: string) => {
    onSelectChat(chatId);
    // alert(window.innerWidth)
    if (window.innerWidth <= 768 ) {
      toggleSidebar();
    }
  };

  useEffect(() => {
    setChatList(chats);  // Update chatList whenever the chats prop changes
  }, [chats]);

  // Click outside handler
  useEffect(() => {
    if (dropdownVisible !== null) {
      const chatItem = chatRefs.current[dropdownVisible];
     
      if (chatItem) {
        const rect = chatItem.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        if (spaceBelow > 115) { // If there's enough space below
          setDropdownPosition({ top: 4, bottom: null }); // Place dropdown below
        } else if (spaceAbove > 115) { // If there's enough space above
          setDropdownPosition({top: null, bottom: 10  }); // Place dropdown above
        }
      }
    }
  }, [dropdownVisible]);
  useEffect(() => {

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef1.current && !dropdownRef1.current.contains(event.target as Node)) {
        setDropdownVisible(null);
      }
    };


    // Only add event listener when dropdown is visible
    if (dropdownVisible) {
      if(!editingChatId){
      document.addEventListener("mousedown", handleClickOutside);
      }
    } else {
      if(!editingChatId){
      document.removeEventListener("mousedown", handleClickOutside);
      }
    }

    // Clean up the event listener when the component is unmounted or dropdown is closed
    return () => {
      if(!editingChatId){
      document.removeEventListener("mousedown", handleClickOutside);
      }
    };
  }, [dropdownVisible,setEditingChatId]); // Only run the effect when `dropdownVisible` changes
  useEffect(() => {
    if (editingChatId) {
      console.log(`Editing Chat ID: ${editingChatId}`); // Log updated editingChatId
    }
  }, [editingChatId]);

  // Add custom scrollbar styles
  useEffect(() => {
    const styleId = 'sidebar-scrollbar-styles';
    let existingStyle = document.getElementById(styleId);
    
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
      existingStyle = style;
    }
    
    const scrollbarColor = effectiveIsLight ? '#c1c1c1' : '#4a4a4a';
    const scrollbarHoverColor = effectiveIsLight ? '#a1a1a1' : '#6a6a6a';
    
    existingStyle.textContent = `
      .scrollbar-thin::-webkit-scrollbar {
        width: 6px;
      }
      .scrollbar-thin::-webkit-scrollbar-track {
        background: transparent;
      }
      .scrollbar-thin::-webkit-scrollbar-thumb {
        background: ${scrollbarColor};
        border-radius: 3px;
      }
      .scrollbar-thin::-webkit-scrollbar-thumb:hover {
        background: ${scrollbarHoverColor};
      }
    `;
  }, [effectiveIsLight]); 

  useEffect(() => {
    if (newTitle) {
      // console.log(`New Title: ${newTitle}`); // Log updated newTitle
    }
  }, [newTitle]);

  const handleRenameClick = (chat:any,e: React.MouseEvent | React.TouchEvent) => {
    setEditingChatId(chat?.id);
    setNewTitle(chat?.title);
  };

  const FetchHistory = async (chatId: string) => {
    try {
      // Step 2: Fetch updated chat history
      const chatData = await fetchChatHistory(app_id || '', net_id);
      const data = chatData.chat_history.history;
  
      // Step 3: Update the title in the data
      const updatedChatData = data.map((chat: ChatData) => {
        if (chat.id === chatId) {
          return {
            ...chat,
            title: newTitle, // Update the title
          };
        }
        return chat;
      });
  
  
      // Step 4: Do something with updatedChatData (e.g., set it in the state)
      // Example: setState(updatedChatData);
      // console.log('Updated chat data:', updatedChatData);
    } catch (error) {
      console.error('Error updating chat title and fetching chat history:', error);
    }
  };
  const handleRenameSubmit = async (chatId : string) => {
    if (!newTitle.trim()) return;
    setIsLoading(true); 
    try {
      
      await updateChatTitle(app_id || '',net_id, chatId, newTitle); // Call API to rename
      // Update the local state to reflect the title change
      const updatedChatList = chatList.map((chat: ChatData) => {
        if (chat.id === chatId) {
          return { ...chat, title: newTitle }; // Update the title locally
        }
        return chat;
      });
      setChatList(updatedChatList); // Update the state
      // Fetch the updated chat history from the parent
      await updateChatHistory(app_id || '', net_id, chatId); // Call the parent method to refresh chat history

      setEditingChatId(null); 
      // FetchHistory(chatId);
    } catch (error) {
      console.error("Failed to rename chat:", error);
    }
    finally {
      setIsLoading(false); // Step 3: Stop loading after the API call
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, chatId: string) => {
    if (event.key === "Enter") {
      handleRenameSubmit(chatId);
    }
  };
  const handleDropdownClick = (chatId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent parent onClick from triggering
    setDropdownVisible(chatId); // Toggle dropdown
  };

  const handleDownload = (chatId: string) => {
    // Find the selected chat by chatId
    const selectedChat = chatList.find((chat) => chat.id === chatId);
    
    if (!selectedChat) {
      console.error("Chat not found!");
      return;
    }
  
    let userIndex = 1;
    let assistantIndex = 1;
  
    // Format the chat messages into a readable string with proper indexing
    const chatContent = selectedChat.messages
      .map((msg) => {
        if (msg.sender === 'user') {
          return `${userIndex++}. \tUser: ${msg.message}`;  // Increment index for user
        } else {
          return ` \tAssistant: ${msg.message}\n`;  // Increment index for assistant
        }
      })
      .join('\n');
  
    // Create a Blob from the formatted chat content
    const blob = new Blob([chatContent], { type: 'text/plain' });
  
    // Create a link to trigger the download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedChat.title || 'chat'}.txt`; // Download file name
    link.click();
  };
  
  

  return (
    <>
      {/* Collapsed Sidebar - Only visible on desktop when closed */}
      {!isSidebarOpen && window.innerWidth > 768 && (
        <div
          className="fixed top-0 left-0 z-30 flex flex-col"
          style={{
            backgroundColor: sidebarBackground || (effectiveIsLight ? '#f7f7f8' : '#171717'),
            height: "100vh",
            width: "52px", // Narrow width like ChatGPT
            borderRight: `1px solid ${borderColor || (effectiveIsLight ? '#e5e5e5' : '#32323b')}`,
            boxShadow: effectiveIsLight 
              ? '1px 0 3px rgba(0, 0, 0, 0.05)' 
              : '1px 0 3px rgba(0, 0, 0, 0.2)'
          }}
        >
          {/* Collapsed Header */}
          <div className="flex flex-col items-center p-2 gap-3" 
               style={{ 
                 backgroundColor: sidebarBackground || (effectiveIsLight ? '#f7f7f8' : '#171717'),
                 borderBottom: `1px solid ${borderColor || (effectiveIsLight ? '#e5e5e5' : '#32323b')}`
               }}>
            {/* Open Sidebar Button */}
            <button 
              data-tooltip-id='open-sidebar' 
              onClick={toggleSidebar} 
              className="p-2 rounded-lg hover:bg-opacity-80 transition-colors duration-200 w-full flex justify-center" 
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
              <div className="h-5 w-5 cursor-pointer relative" style={{ color: textColor || (effectiveIsLight ? '#333333' : '#ececf1') }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
                  <path fillRule="evenodd" clipRule="evenodd" d="M8.85719 3L13.5 3C14.0523 3 14.5 3.44772 14.5 4C14.5 4.55229 14.0523 5 13.5 5H11.5V19H15.1C16.2366 19 17.0289 18.9992 17.6458 18.9488C18.2509 18.8994 18.5986 18.8072 18.862 18.673C19.4265 18.3854 19.8854 17.9265 20.173 17.362C20.3072 17.0986 20.3994 16.7509 20.4488 16.1458C20.4992 15.5289 20.5 14.7366 20.5 13.6V11.5C20.5 10.9477 20.9477 10.5 21.5 10.5C22.0523 10.5 22.5 10.9477 22.5 11.5V13.6428C22.5 14.7266 22.5 15.6008 22.4422 16.3086C22.3826 17.0375 22.2568 17.6777 21.955 18.27C21.4757 19.2108 20.7108 19.9757 19.77 20.455C19.1777 20.7568 18.5375 20.8826 17.8086 20.9422C17.1008 21 16.2266 21 15.1428 21H8.85717C7.77339 21 6.89925 21 6.19138 20.9422C5.46253 20.8826 4.82234 20.7568 4.23005 20.455C3.28924 19.9757 2.52433 19.2108 2.04497 18.27C1.74318 17.6777 1.61737 17.0375 1.55782 16.3086C1.49998 15.6007 1.49999 14.7266 1.5 13.6428V10.3572C1.49999 9.27341 1.49998 8.39926 1.55782 7.69138C1.61737 6.96253 1.74318 6.32234 2.04497 5.73005C2.52433 4.78924 3.28924 4.02433 4.23005 3.54497C4.82234 3.24318 5.46253 3.11737 6.19138 3.05782C6.89926 2.99998 7.77341 2.99999 8.85719 3ZM9.5 19V5H8.9C7.76339 5 6.97108 5.00078 6.35424 5.05118C5.74907 5.10062 5.40138 5.19279 5.13803 5.32698C4.57354 5.6146 4.1146 6.07354 3.82698 6.63803C3.69279 6.90138 3.60062 7.24907 3.55118 7.85424C3.50078 8.47108 3.5 9.26339 3.5 10.4V13.6C3.5 14.7366 3.50078 15.5289 3.55118 16.1458C3.60062 16.7509 3.69279 17.0986 3.82698 17.362C4.1146 17.9265 4.57354 18.3854 5.13803 18.673C5.40138 18.8072 5.74907 18.8994 6.35424 18.9488C6.97108 18.9992 7.76339 19 8.9 19H9.5ZM5 8.5C5 7.94772 5.44772 7.5 6 7.5H7C7.55229 7.5 8 7.94772 8 8.5C8 9.05229 7.55229 9.5 7 9.5H6C5.44772 9.5 5 9.05229 5 8.5ZM5 12C5 11.4477 5.44772 11 6 11H7C7.55229 11 8 11.4477 8 12C8 12.5523 7.55229 13 7 13H6C5.44772 13 5 12.5523 5 12Z" fill="currentColor"></path>
                  <circle cx="20" cy="5" r="4" fill="#0285FF"></circle>
                </svg>
              </div>
            </button>
            <Tooltip id='open-sidebar' place="right">Open sidebar</Tooltip>
            
            {/* New Chat Button */}
            <button 
              data-tooltip-id='new-chat-collapsed' 
              className="p-2 rounded-lg hover:bg-opacity-80 transition-colors duration-200 w-full flex justify-center" 
              onClick={handleNewChat} 
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
              <Edit className="h-5 w-5" style={{ color: textColor || (effectiveIsLight ? '#333333' : '#ececf1') }} />
            </button>
            <Tooltip id='new-chat-collapsed' place="right">New chat</Tooltip>
          </div>

          {/* Collapsed Sidebar Bottom - User Icon */}
          <div className="flex flex-col items-center mt-auto p-2" 
               style={{ 
                 backgroundColor: sidebarBackground || (effectiveIsLight ? '#f7f7f8' : '#171717'),
                 borderTop: `1px solid ${borderColor || (effectiveIsLight ? '#e5e5e5' : '#32323b')}`
               }}>
            <button
              data-tooltip-id='user-profile-collapsed'
              onClick={() => setCollapsedModalOpen(!collapsedModalOpen)}
              className="relative rounded-lg hover:bg-opacity-80 transition-colors duration-200 flex items-center justify-center"
              style={{ 
                width: '36px', 
                height: '36px',
                padding: '0'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
            </button>
            <Tooltip id='user-profile-collapsed' place="right">
              {localStorage.getItem('username') || 'User Profile'}
            </Tooltip>
          </div>
        </div>
      )}

      {/* ChatGPT-style Modal for Collapsed Sidebar User Profile */}
      {collapsedModalOpen && !isSidebarOpen && window.innerWidth > 768 && (
        <>
          {/* Invisible Backdrop for Click Outside */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setCollapsedModalOpen(false)}
          />
          
          {/* Modal Content - Positioned above user icon */}
          <div 
            className="fixed z-50 rounded-lg shadow-xl w-80"
            style={{
              left: '60px', // Position to the right of collapsed sidebar (52px + 8px margin)
              bottom: '20px', // Position above the user icon
              backgroundColor: themeOverride === 'auto' 
                ? sidebarBackground 
                : (effectiveIsLight ? '#ffffff' : '#2c2c2c'),
              border: `1px solid ${borderColor}`,
              boxShadow: effectiveIsLight 
                ? '0 10px 25px rgba(0, 0, 0, 0.15)' 
                : '0 10px 25px rgba(0, 0, 0, 0.4)'
            }}
          >
            {/* Modal Header */}
            <div className="p-4 border-b" style={{ borderColor: borderColor }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-sm font-medium" style={{ color: textColor }}>
                    {localStorage.getItem('username') || 'User'}
                  </div>
                  <div className="text-xs opacity-70" style={{ color: textColor }}>
                    {netId || 'Guest'}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              {/* User Message Count - Only show if not in bypass mode */}
              {!bypassMode && !noAuth && (
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                   <span className="text-xs flex items-center gap-1" style={{ color: textColor }}>
                      Messages Left
                      <span className="relative group cursor-pointer">
                        <Info className="h-3 w-3 opacity-60" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 w-max max-w-xs text-xs bg-black text-white rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 whitespace-nowrap">
                          Limit resets monthly
                        </div>
                      </span>
                    </span>
                    <span 
                      className="font-bold text-xs"
                      style={{
                        color: ((parseInt(configData?.messages_per_month_limit || "0") - userMsgCount) / 
                               (parseInt(configData?.messages_per_month_limit || "1"))) * 100 < 10 
                               ? "#e74c3c" : textColor
                      }}
                    >
                      {parseInt(configData?.messages_per_month_limit || "0") - userMsgCount}
                      /{parseInt(configData?.messages_per_month_limit || "0")}
                    </span>
                  </div>
                
                </div>
              )}

              {/* Theme Selection */}
              <div className="mb-4">
                <label className="block text-xs font-medium mb-2" style={{ color: textColor }}>
                  Theme
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => {
                      setThemeOverride('auto');
                      setCollapsedModalOpen(false);
                    }}
                    className={`flex items-center justify-center gap-2 p-2 rounded-md text-xs transition-colors duration-200 ${
                      themeOverride === 'auto' ? 'ring-2 ring-blue-500' : ''
                    }`}
                    style={{
                      backgroundColor: themeOverride === 'auto' 
                        ? (effectiveIsLight ? '#f3f4f6' : '#374151')
                        : (effectiveIsLight ? '#f9fafb' : '#2d2d30'),
                      color: textColor
                    }}
                  >
                    <Monitor className="h-3 w-3" />
                    Auto
                  </button>
                  <button
                    onClick={() => {
                      setThemeOverride('light');
                      setCollapsedModalOpen(false);
                    }}
                    className={`flex items-center justify-center gap-2 p-2 rounded-md text-xs transition-colors duration-200 ${
                      themeOverride === 'light' ? 'ring-2 ring-blue-500' : ''
                    }`}
                    style={{
                      backgroundColor: themeOverride === 'light' 
                        ? (effectiveIsLight ? '#f3f4f6' : '#374151')
                        : (effectiveIsLight ? '#f9fafb' : '#2d2d30'),
                      color: textColor
                    }}
                  >
                    <Sun className="h-3 w-3" />
                    Light
                  </button>
                  <button
                    onClick={() => {
                      setThemeOverride('dark');
                      setCollapsedModalOpen(false);
                    }}
                    className={`flex items-center justify-center gap-2 p-2 rounded-md text-xs transition-colors duration-200 ${
                      themeOverride === 'dark' ? 'ring-2 ring-blue-500' : ''
                    }`}
                    style={{
                      backgroundColor: themeOverride === 'dark' 
                        ? (effectiveIsLight ? '#f3f4f6' : '#374151')
                        : (effectiveIsLight ? '#f9fafb' : '#2d2d30'),
                      color: textColor
                    }}
                  >
                    <Moon className="h-3 w-3" />
                    Dark
                  </button>
                </div>
              </div>

              {/* Logout Button */}
              {!bypassMode && !noAuth && (
                <div className="pt-3 border-t" style={{ borderColor: borderColor }}>
                  <button
                    onClick={() => {
                      setCollapsedModalOpen(false);
                      onLogout();
                    }}
                    className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-opacity-100 transition-colors duration-200"
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    style={{ color: textColor }}
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Full Sidebar - Only visible when opened */}
      <div
        ref={sidebarRef}
        className={`sidebar transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'w-64 sm:w-1/3 md:w-64' : 'w-0'}
          fixed top-0 left-0 z-30 flex flex-col relative
          ${!isSidebarOpen && 'hidden'}
          ${isSidebarOpen && window.innerWidth <= 768 && 'inset-0 bg-black bg-opacity-50 z-50'}`}
        style={{
          backgroundColor: sidebarBackground,
          height: "100vh",
          overflowY: "hidden",
          transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          borderRight: `1px solid ${borderColor}`,
        }}
      >
  {/* Sidebar Header - ChatGPT style */}
  <div className="sidebar-header flex justify-between items-center p-3 sticky top-0 z-40" 
       style={{ 
         backgroundColor: sidebarBackground,
         borderBottom: `1px solid ${borderColor}`
       }}>
    <button 
      data-tooltip-id='menu' 
      onClick={toggleSidebar} 
      className="p-2 rounded-lg hover:bg-opacity-80 transition-colors duration-200" 
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
      <div
        className="h-5 w-5 cursor-pointer"
        style={{ color: textColor }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-full w-full"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.85719 3L13.5 3C14.0523 3 14.5 3.44772 14.5 4C14.5 4.55229 14.0523 5 13.5 5H11.5V19H15.1C16.2366 19 17.0289 18.9992 17.6458 18.9488C18.2509 18.8994 18.5986 18.8072 18.862 18.673C19.4265 18.3854 19.8854 17.9265 20.173 17.362C20.3072 17.0986 20.3994 16.7509 20.4488 16.1458C20.4992 15.5289 20.5 14.7366 20.5 13.6V11.5C20.5 10.9477 20.9477 10.5 21.5 10.5C22.0523 10.5 22.5 10.9477 22.5 11.5V13.6428C22.5 14.7266 22.5 15.6008 22.4422 16.3086C22.3826 17.0375 22.2568 17.6777 21.955 18.27C21.4757 19.2108 20.7108 19.9757 19.77 20.455C19.1777 20.7568 18.5375 20.8826 17.8086 20.9422C17.1008 21 16.2266 21 15.1428 21H8.85717C7.77339 21 6.89925 21 6.19138 20.9422C5.46253 20.8826 4.82234 20.7568 4.23005 20.455C3.28924 19.9757 2.52433 19.2108 2.04497 18.27C1.74318 17.6777 1.61737 17.0375 1.55782 16.3086C1.49998 15.6007 1.49999 14.7266 1.5 13.6428V10.3572C1.49999 9.27341 1.49998 8.39926 1.55782 7.69138C1.61737 6.96253 1.74318 6.32234 2.04497 5.73005C2.52433 4.78924 3.28924 4.02433 4.23005 3.54497C4.82234 3.24318 5.46253 3.11737 6.19138 3.05782C6.89926 2.99998 7.77341 2.99999 8.85719 3ZM9.5 19V5H8.9C7.76339 5 6.97108 5.00078 6.35424 5.05118C5.74907 5.10062 5.40138 5.19279 5.13803 5.32698C4.57354 5.6146 4.1146 6.07354 3.82698 6.63803C3.69279 6.90138 3.60062 7.24907 3.55118 7.85424C3.50078 8.47108 3.5 9.26339 3.5 10.4V13.6C3.5 14.7366 3.50078 15.5289 3.55118 16.1458C3.60062 16.7509 3.69279 17.0986 3.82698 17.362C4.1146 17.9265 4.57354 18.3854 5.13803 18.673C5.40138 18.8072 5.74907 18.8994 6.35424 18.9488C6.97108 18.9992 7.76339 19 8.9 19H9.5ZM5 8.5C5 7.94772 5.44772 7.5 6 7.5H7C7.55229 7.5 8 7.94772 8 8.5C8 9.05229 7.55229 9.5 7 9.5H6C5.44772 9.5 5 9.05229 5 8.5ZM5 12C5 11.4477 5.44772 11 6 11H7C7.55229 11 8 11.4477 8 12C8 12.5523 7.55229 13 7 13H6C5.44772 13 5 12.5523 5 12Z"
            fill="currentColor"
          ></path>
          <circle cx="20" cy="5" r="4" fill="#0285FF"></circle>
        </svg>
      </div>
    </button>
    <Tooltip id='menu' place="right">
      Close sidebar
    </Tooltip>
    {isSidebarOpen && (
      <>
        <button 
          data-tooltip-id='newchat' 
          className="p-2 rounded-lg hover:bg-opacity-80 transition-colors duration-200" 
          onClick={handleNewChat} 
          style={{color: textColor}}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
          <Edit className="h-5 w-5" style={{ color: textColor }} />
        </button>
        <Tooltip id='newchat' place="bottom">
          New Chat
        </Tooltip>
      </>
    )}
  </div>

  {isSidebarOpen && (
    <div className="flex-1 flex flex-col">
      {/* Chat sections container - scrollable area */}
      <div 
        className="flex-1 overflow-y-auto px-0 scrollbar-thin" 
        style={{ 
          maxHeight: "calc(100vh - 140px)",
          paddingBottom: "80px",
          scrollbarWidth: "thin",
          scrollbarColor: `${effectiveIsLight ? '#c1c1c1' : '#4a4a4a'} transparent`
        }}
      >
        {chatSections?.map(({ label, chats }) =>
        chats?.length > 0 ? (
          <div key={label} className="chat-section mb-4">
            {/* Section header - sticky within this section only */}
            <div 
              className="sticky z-20"
              style={{ 
                top: "0px",
                backgroundColor: sidebarBackground,
              }}
            >
              <h3 
                className="px-3 py-2 text-xs font-semibold text-gray-500" 
                style={{ 
                  color: effectiveIsLight ? '#666666' : '#8e8ea0',
                }}
              >
                {label}
              </h3>
            </div>
            
            {/* Chat list for this section */}
            <ul className="mt-2">
              {chats?.map((chat) => (
                <li
                  key={chat.id}
                  ref={(el) => (chatRefs.current[chat.id] = el)}
                  id={`chat-item-${chat.id}`}
                  onClick={() => handleChatSelect(chat.id)}
                  onMouseEnter={(e) => {
                    setHoveredChatId(chat.id);
                    e.currentTarget.style.backgroundColor = hoverColor;
                  }}
                  onMouseLeave={(e) => {
                    setHoveredChatId(null);
                    e.currentTarget.style.backgroundColor = activeChatId === chat.id ? activeColor : "transparent";
                  }}
                  onTouchStart={() => setHoveredChatId(chat.id)}
                  className={`relative cursor-pointer rounded-lg p-3 mx-2 my-1 transition-colors duration-200`}
                  style={{ 
                    color: textColor,
                    backgroundColor: activeChatId === chat.id ? activeColor : "transparent",
                    border: activeChatId === chat.id && themeOverride === 'auto' 
                      ? ` ${configIsLight ? darkenColor(backgroundColor, 20) : lightenColor(backgroundColor, 50)}` 
                      : '1px solid transparent',
                  }}
                >
                  {/* Title inside list item */}
                  <div className="flex-1 overflow-hidden whitespace-nowrap">
                    {editingChatId === chat.id ? (
                      <div className="relative w-[90%]">
                        {/* Input field */}
                        <input
                          type="text"
                          className="w-full text-sm p-1" // Add padding-right for loader
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          onBlur={() => setEditingChatId(null)}
                          onKeyDown={(e) => handleKeyDown(e, chat.id)}
                          autoFocus
                          style={{
                            color: configData?.app_ui_settings.ui_fontcolor,
                            background: configData?.app_ui_settings.ui_backgroundcolor,
                          }}
                        />
                      
                        {/* Loader (appears at the end of the input) */}
                        {isLoading && (
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <div className="loader" style={{
                              borderTop: `4px solid ${configData?.app_ui_settings.ui_fontcolor}`,
                              borderRight: `4px solid ${darkerTabBackground}`,
                              borderBottom: `4px solid ${darkerTabBackground}`,
                              borderLeft: `4px solid ${darkerTabBackground}`,
                            }}></div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span 
                        className="block w-[90%] overflow-hidden text-sm font-medium truncate" 
                        onDoubleClick={(e) => handleRenameClick(chat, e)}
                        title={chat.title}
                      >
                        {chat.title}
                      </span>
                    )}
                  </div>
                
                  {hoveredChatId === chat.id && !isPreview && (
                    <div 
                      ref={dropdownRef} 
                      className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2 z-30" // Modified: added z-30
                      style={{
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <AiOutlineEllipsis
                        className="text-lg cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent the click from propagating
                          
                          // Calculate dropdown position based on available space
                          const chatItemElement = chatRefs.current[chat.id];
                          const chatItemRect = chatItemElement?.getBoundingClientRect();
                          
                          if (chatItemRect) {
                            // Check if we're near the bottom of the view
                            const spaceBelow = window.innerHeight - chatItemRect.bottom;
                            const dropdownHeight = 120; // Approximate height of dropdown menu
                            
                            if (spaceBelow < dropdownHeight) {
                              // Position above if close to bottom
                              setDropdownPosition({
                                top: 'auto',
                                bottom: '100%',
                              });
                            } else {
                              // Default position below
                              setDropdownPosition({
                                top: '2rem',
                                bottom: 'auto',
                              });
                            }
                          }
                          
                          setDropdownVisible(dropdownVisible === chat.id ? null : chat.id);
                        }}
                      />
                    </div>
                  )}
                   
                  {dropdownVisible === chat.id && !isPreview && (
                    <div
                      ref={dropdownRef1}
                      className="absolute right-1 z-50 border border-gray-300 rounded-md shadow-lg w-32" // Modified: z-50 to ensure dropdown is on top
                      onClick={(e) => {
                        e.stopPropagation();
                          setDropdownVisible(dropdownVisible === chat.id ? null : chat.id);
                        }}// Prevent click from closing dropdown
                      style={{
                        background: sidebarBackground,
                        border: `1px solid ${borderColor}`,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                        top: dropdownPosition.top,
                        bottom: dropdownPosition.bottom,
                      }}
                    >
                      <button
                        className="w-[80%] text-left ml-2 mt-2 pt-2 pb-2 pl-1 ml-3 pr-2 text-sm hover:rounded-lg flex items-center gap-2"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        onClick={(e) => {
                          handleDownload(chat.id)
                        }}
                        onTouchEnd={(e) => handleDownload(chat.id)}
                        style={{
                          color: textColor,
                        }}
                      >
                        <Download className="h-4 w-4" style={{ color: textColor }} />
                        <span>Download </span>
                      </button>
                      <button
                        className="w-[80%] text-left ml-2 mt-2 pt-2 pb-2 pl-1 ml-3 pr-2 text-sm hover:rounded-lg flex items-center gap-2"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        onClick={(e) => handleRenameClick(chat, e)}
                        onTouchEnd={(e) => handleRenameClick(chat, e)}
                        style={{
                          color: textColor,
                        }}
                      >
                        <Pencil className="h-4 w-4" style={{ color: textColor }} />
                        <span>Rename</span>
                      </button>

                      <button
                        className="w-[80%] text-left ml-2 mt-2 pt-2 pb-2 pl-1 ml-3 pr-2 mb-1 text-sm hover:rounded-lg flex items-center gap-2"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        onClick={() => onDelete(chat.id)}
                        onTouchEnd={(e) => onDelete(chat.id)}
                        style={{
                          color: '#ef4444',
                        }}
                      >
                        <Trash className="h-4 w-4" style={{ color: '#ef4444' }}/>
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null
      )}
      </div>

      {/* User Profile Section - Fixed to Bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-3 border-t" style={{ 
        backgroundColor: sidebarBackground,
        borderColor: borderColor 
      }}>
      <div className="relative" ref={userProfileRef}>
        <button
          onClick={() => setUserProfileOpen(!userProfileOpen)}
          className="flex items-center gap-3 w-full p-2 rounded-lg transition-colors duration-200 hover:bg-opacity-80"
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <User className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-sm font-medium" style={{ color: textColor }}>
              {localStorage.getItem('username') || 'User'}
            </div>
            <div className="text-xs opacity-70" style={{ color: textColor }}>
              {netId || 'Guest'} • Theme: {themeOverride === 'auto' ? 'Auto' : themeOverride === 'light' ? 'Light' : 'Dark'}
            </div>
          </div>
          <Settings className="h-4 w-4 opacity-60" style={{ color: textColor }} />
        </button>

        {/* User Profile Dropdown */}
        {userProfileOpen && (
          <div 
            className="absolute bottom-full left-0 right-3 mb-2 rounded-lg border shadow-lg z-50"
            style={{
              backgroundColor: themeOverride === 'auto' 
                ? sidebarBackground 
                : (effectiveIsLight ? '#ffffff' : '#2c2c2c'),
              borderColor: borderColor,
              boxShadow: effectiveIsLight 
                ? '0 4px 16px rgba(0, 0, 0, 0.1)' 
                : '0 4px 16px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="p-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-sm font-medium" style={{ color: textColor }}>
                    {localStorage.getItem('username') || 'User'}
                  </div>
                  <div className="text-xs opacity-70" style={{ color: textColor }}>
                    {netId || 'Guest'}
                  </div>
                </div>
              </div>

              {/* User Message Count - Only show if not in bypass mode */}
              {!bypassMode && !noAuth && (
                <div className="mb-3 border-t pt-3" style={{ borderColor: borderColor }}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs flex items-center gap-1" style={{ color: textColor }}>
                      Messages Left
                      <span className="relative group cursor-pointer">
                        <Info className="h-3 w-3 opacity-60" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 w-max max-w-xs text-xs bg-black text-white rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 whitespace-nowrap">
                          Limit resets monthly
                        </div>
                      </span>
                    </span>
                    <span 
                      className="font-bold text-xs"
                      style={{
                        color: ((parseInt(configData?.messages_per_month_limit || "0") - userMsgCount) / 
                               (parseInt(configData?.messages_per_month_limit || "1"))) * 100 < 10 
                               ? "#e74c3c" : textColor
                      }}
                    >
                      {parseInt(configData?.messages_per_month_limit || "0") - userMsgCount}
                      /{parseInt(configData?.messages_per_month_limit || "0")}
                    </span>
                  </div>
                </div>
              )}

              <div className="border-t pt-3" style={{ borderColor: borderColor }}>
                <div className="mb-2">
                  <div className="text-xs font-medium mb-2 flex items-center gap-2" style={{ color: textColor }}>
                    <Palette className="h-3 w-3" />
                    Theme
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setThemeOverride('auto')}
                      className={`flex-1 p-1.5 rounded text-xs flex items-center justify-center gap-1 transition-colors ${
                        themeOverride === 'auto' ? 'opacity-100' : 'opacity-60'
                      }`}
                      style={{ 
                        backgroundColor: themeOverride === 'auto' ? activeColor : 'transparent',
                        color: textColor 
                      }}
                    >
                      <Monitor className="h-3 w-3" />
                      Auto
                    </button>
                    <button
                      onClick={() => setThemeOverride('light')}
                      className={`flex-1 p-1.5 rounded text-xs flex items-center justify-center gap-1 transition-colors ${
                        themeOverride === 'light' ? 'opacity-100' : 'opacity-60'
                      }`}
                      style={{ 
                        backgroundColor: themeOverride === 'light' ? activeColor : 'transparent',
                        color: textColor 
                      }}
                    >
                      <Sun className="h-3 w-3" />
                      Light
                    </button>
                    <button
                      onClick={() => setThemeOverride('dark')}
                      className={`flex-1 p-1.5 rounded text-xs flex items-center justify-center gap-1 transition-colors ${
                        themeOverride === 'dark' ? 'opacity-100' : 'opacity-60'
                      }`}
                      style={{ 
                        backgroundColor: themeOverride === 'dark' ? activeColor : 'transparent',
                        color: textColor 
                      }}
                    >
                      <Moon className="h-3 w-3" />
                      Dark
                    </button>
                  </div>
                </div>

                
              </div>
              
            </div>
            {/* Logout Button - Only show if not in bypass mode */}
                {!bypassMode && !noAuth && (
                  <div className="mt-1 pt-1 border-t p-2" style={{ borderColor: borderColor }}>
                    <button
  onClick={onLogout}
  className="flex items-center gap-2 w-full rounded-lg transition-colors duration-200 px-3 py-2"
  style={{
    color: textColor,
    paddingRight: "60px" // or "20%" for responsive
  }}
  
  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
>
  <LogOut className="h-4 w-4" />
  <span>Logout</span>
</button>
                  </div>
                )}
          </div>
          
        )}
      </div>
    </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ChatSidebar;
